import { useContext, useState } from "react";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { downloadProject } from "../../services/projectService";
import {
  DownloadProjectOrFolderResponse,
  Folder,
  ProjectFile,
} from "../../proto/api_pb";
import { toast } from "react-toastify";
import JSZip from "jszip";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { CircularProgress, MenuItem } from "@mui/material";

export interface DownloadFolderOptionProps {
  folder: Folder;
}

export const DownloadFolderOption: React.FC<DownloadFolderOptionProps> = ({
  folder,
}) => {
  const [loading, setLoading] = useState(false);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise } = useContext(UserDetailsContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const downloadEntireFolder = async (folder: any) => {
    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const res = await downloadProject(
        token,
        enterprise?.id as string,
        userId,
        "", // no project id
        folder.getId()! // target folder id
      );

      if (!(res instanceof DownloadProjectOrFolderResponse)) {
        console.error("Failed to download folder", res);
        return;
      }

      const projectFiles = res.getProjectFilesList();
      const trackFiles = res.getTracksList();
      const folders = res.getFoldersList();

      if (!projectFiles.length && !trackFiles.length && !folders.length) {
        toast.warn("No files, tracks, or folders found in this folder");
        return;
      }

      const zip = new JSZip();

      // ------------ Build folder map ------------
      type FolderLite = { id: string; name: string; parentId?: string };
      const folderMap: Record<string, FolderLite> = {};
      folders.forEach((f) => {
        folderMap[f.getId()] = {
          id: f.getId(),
          name: f.getName(),
          parentId: f.getParentFolderId?.() || "",
        };
      });

      const rootFolderId = folder.getId(); // 👈 treat this as ZIP root
      const folderPathCache = new Map<string, string>();

      const safeName = (s: string) =>
        (s || "")
          .replace(/[\\?%*:|"<>/]/g, "_")
          .replace(/\s+/g, " ")
          .trim();

      const getFolderPath = (folderId?: string): string => {
        if (!folderId) return "";
        if (folderPathCache.has(folderId))
          return folderPathCache.get(folderId)!;

        const seen = new Set<string>();
        const parts: string[] = [];
        let current = folderId;

        while (current && folderMap[current]) {
          if (seen.has(current)) break;
          seen.add(current);

          // 👇 stop at the root folder (don't include it in path)
          if (current === rootFolderId) break;

          const f = folderMap[current];
          parts.unshift(safeName(f.name));
          current = f.parentId || "";
        }

        const p = parts.join("/");
        folderPathCache.set(folderId, p);
        return p;
      };

      const usedPaths = new Map<string, number>();
      const getUniquePath = (basePath: string): string => {
        if (!usedPaths.has(basePath)) {
          usedPaths.set(basePath, 1);
          return basePath;
        }
        const count = usedPaths.get(basePath)!;
        usedPaths.set(basePath, count + 1);

        const extMatch = basePath.match(/(\.[^.]*)$/);
        const ext = extMatch ? extMatch[1] : "";
        const nameWithoutExt = ext ? basePath.slice(0, -ext.length) : basePath;

        return `${nameWithoutExt} (${count})${ext}`;
      };

      // ------------ Folder/file/track helpers ------------
      const getFolderIdsFromFile = (file: any): string[] => {
        if (typeof file.getParentFolderIdList === "function") {
          return (file.getParentFolderIdList() || []).filter(Boolean);
        }
        if (typeof file.getFolderIdsList === "function") {
          return (file.getFolderIdsList() || []).filter(Boolean);
        }
        if (typeof file.getParentFolderId === "function") {
          const v = file.getParentFolderId();
          return v ? [v] : [];
        }
        if (typeof file.getFolderId === "function") {
          const v = file.getFolderId();
          return v ? [v] : [];
        }
        return [];
      };

      const getFolderIdsFromTrack = (track: any): string[] => {
        if (!track) return [];
        if (typeof track.getParentFolderIdList === "function") {
          return (track.getParentFolderIdList() || []).filter(Boolean);
        }
        if (typeof track.getFolderIdsList === "function") {
          return (track.getFolderIdsList() || []).filter(Boolean);
        }
        if (typeof track.getParentFolderId === "function") {
          const v = track.getParentFolderId();
          return v ? [v] : [];
        }
        if (typeof track.getFolderId === "function") {
          const v = track.getFolderId();
          return v ? [v] : [];
        }
        return [];
      };

      const blobCache = new Map<string, Promise<Blob>>();
      const fetchBlobOnce = (url: string): Promise<Blob> => {
        if (!blobCache.has(url)) {
          blobCache.set(
            url,
            (async () => {
              const resp = await fetch(url);
              if (!resp.ok) throw new Error(`Failed to fetch: ${url}`);
              return await resp.blob();
            })()
          );
        }
        return blobCache.get(url)!;
      };

      // ------------ Project files inside folder ------------
      const projectFilePromises = projectFiles.map(
        async (file: ProjectFile) => {
          const fileUrl = file.getFileUrl();
          const baseName = safeName(file.getFileName?.() || "file");
          if (!fileUrl) return;
          try {
            const blob = await fetchBlobOnce(fileUrl);
            const folderIds = getFolderIdsFromFile(file);
            if (!folderIds.length) {
              zip.file(getUniquePath(baseName), blob);
              return;
            }
            for (const fid of folderIds) {
              const fp = getFolderPath(fid);
              const path = fp ? `${fp}/${baseName}` : baseName;
              zip.file(getUniquePath(path), blob);
            }
          } catch (err) {
            console.error(`Error downloading file ${baseName}:`, err);
          }
        }
      );

      // ------------ Tracks inside folder ------------
      const trackFilePromises = trackFiles.map(async (trackResponse: any) => {
        const trackVersion = trackResponse.getLatestTrackVersion?.();
        const track = trackResponse.getTrack?.();
        if (!trackVersion) return;
        const trackUrl: string | undefined = trackVersion.getTrackUrl?.();
        if (!trackUrl) return;

        const deriveExt = (
          url: string,
          contentType?: string | null
        ): string => {
          let ext = (() => {
            const parts = url.split(".");
            if (parts.length > 1) {
              return `.${parts[parts.length - 1].split("?")[0]}`;
            }
            return ".mp3";
          })();
          if (contentType) {
            const mimeToExt: Record<string, string> = {
              "audio/mpeg": ".mp3",
              "audio/wav": ".wav",
              "audio/x-wav": ".wav",
              "audio/ogg": ".ogg",
              "audio/flac": ".flac",
              "audio/aac": ".aac",
              "audio/mp4": ".m4a",
            };
            for (const [mime, e] of Object.entries(mimeToExt)) {
              if (contentType.includes(mime)) {
                ext = e;
                break;
              }
            }
          }
          return ext;
        };

        try {
          const resp = await fetch(trackUrl);
          if (!resp.ok) throw new Error(`Failed to fetch track: ${trackUrl}`);
          const trackBlob = await resp.blob();

          const contentType = resp.headers.get("content-type");
          const ext = deriveExt(trackUrl, contentType);
          const trackTitle = safeName(
            track?.getTitle?.() || "Unknown"
          ).substring(0, 50);
          const finalTrackName = `${trackTitle}${ext}`;

          const folderIds = getFolderIdsFromTrack(track);
          if (!folderIds.length) {
            zip.file(getUniquePath(finalTrackName), trackBlob);
          } else {
            for (const fid of folderIds) {
              const fp = getFolderPath(fid);
              const path = fp ? `${fp}/${finalTrackName}` : finalTrackName;
              zip.file(getUniquePath(path), trackBlob);
            }
          }
        } catch (err) {
          console.error("Error downloading track:", err);
        }
      });

      await Promise.all([...projectFilePromises, ...trackFilePromises]);

      // ------------ Generate & download zip ------------
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${folder.getName()}.zip`; // <- folder name here
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Folder is ready to download!");
    } catch (error) {
      console.error("Error downloading folder:", error);
      toast.error("Failed to download folder.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <MenuItem onClick={() => downloadEntireFolder(folder)}>
        {loading ? (
          <CircularProgress sx={{ color: "white" }} size={20} />
        ) : (
          "Download"
        )}
      </MenuItem>
    </>
  );
};
